import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Plus, Eye, FileText, Download, Printer, X, PackageOpen, RotateCcw, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { useTenant } from '../contexts/TenantContext';
import { useToast } from '../hooks/useToast';
import { api } from '../lib/api';
import GlassTable from '../components/GlassTable';
import { GlassCard } from '../components/GlassCard';
import Drawer from '../components/Drawer';

const INPUT = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition-colors';
const PAGE_SIZE = 25;

export default function PurchaseOrders() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMoney, currencySymbol } = useTenantFormat();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [viewItem, setViewItem] = useState(null);
  const [pdfPoId, setPdfPoId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const res = await api.purchaseOrdersList(token, { page_size: PAGE_SIZE, offset });
      setItems(res.items || []);
      setCount(res.count || 0);
    } catch (e) {
      if (e.status !== 401) toast.show(e.message || 'Failed to load', 'error');
    } finally { setLoading(false); }
  }, [token, page, toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(po =>
      `${po.supplier_name || ''} ${po.po_number || ''}`.toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const columns = [
    { key: 'po_number', label: 'PO #', render: (_, row) => <span className="font-semibold text-gray-900" data-testid={`po-number-${row.id}`}>{row.po_number || '—'}</span> },
    { key: 'supplier_name', label: 'Supplier', render: (_, row) => <div><span>{row.supplier_name || '—'}</span>{row.supplier_phone && <span className="block text-gray-400 text-[11px]">{row.supplier_phone}</span>}</div> },
    { key: 'created_at', label: 'Date', render: (_, row) => { const d = row.created_at || row.date; return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'; } },
    { key: 'items_count', label: 'Items', render: (_, row) => (row.items || []).length },
    { key: 'total', label: `Total (${currencySymbol})`, render: (_, row) => <span className="tabular-nums font-semibold">{formatMoney(row.total)}</span> },
    { key: 'status', label: 'Status', render: (_, row) => (
      <div className="flex flex-col gap-1">
        <StatusPill status={row.status} />
        {row.total_qty_returned > 0 && (
          <span className="inline-block px-2 py-0.5 rounded-full text-[9px] font-bold bg-violet-50 text-violet-600 border border-violet-200" data-testid={`po-returned-chip-${row.id}`}>
            Returned: {row.total_qty_returned} of {row.total_qty_ordered || (row.items || []).reduce((s, l) => s + (l.qty || 0), 0)}
          </span>
        )}
      </div>
    ) },
  ];

  if (!loading && items.length === 0 && !search) {
    return (
      <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="purchase-orders-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-gray-900 font-extrabold text-xl">Purchase Orders</h1>
        </div>
        <GlassCard className="p-12 text-center" data-testid="purchase-orders-empty">
          <PackageOpen size={48} className="text-gray-300 mx-auto mb-4" />
          <h3 className="text-gray-900 font-bold text-lg mb-2">No purchase orders yet</h3>
          <p className="text-gray-500 text-sm mb-4">Create your first purchase order to get started.</p>
          <button onClick={() => setNewOpen(true)} data-testid="new-po-empty-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all"><Plus size={16} /> New PO</button>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-gray-900 font-extrabold text-xl">Purchase Orders</h1>
        <button
          onClick={() => setNewOpen(true)}
          data-testid="new-po-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all border-0"
        >
          <Plus size={16} /> New PO
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          data-testid="po-search"
          type="text"
          placeholder="Search supplier or PO number…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full max-w-sm h-11 px-4 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm placeholder:text-gray-400 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none"
        />
      </div>

      {/* Table */}
      <GlassTable
        columns={columns}
        data={filtered}
        loading={loading}
        onRowClick={row => setViewItem(row)}
        testId="purchase-orders-table"
        actions={row => (
          <>
            <button data-testid={`view-po-${row.id}`} onClick={e => { e.stopPropagation(); setViewItem(row); }} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title="View details"><Eye size={16} /></button>
            <button data-testid={`pdf-po-${row.id}`} onClick={e => { e.stopPropagation(); setPdfPoId(row.id); }} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-brand-purple transition-colors" title="Preview PO PDF"><FileText size={16} /></button>
            <button data-testid={`dn-po-${row.id}`} onClick={e => { e.stopPropagation(); navigate(`/notes/debit/new?po_id=${row.id}`); }} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-violet-500 transition-colors" title="Create Debit Note"><RotateCcw size={16} /></button>
          </>
        )}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <span className="text-gray-500 text-sm">{count} purchase order{count !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <span className="px-3 py-1.5 text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      <Drawer open={!!viewItem} onClose={() => setViewItem(null)} title={`PO ${viewItem?.po_number || ''}`}>
        {viewItem && <PoDetail po={viewItem} formatMoney={formatMoney} />}
      </Drawer>

      {/* New PO Drawer */}
      <NewPODrawer open={newOpen} onClose={() => setNewOpen(false)} token={token} onSuccess={() => { setNewOpen(false); load(); }} />

      {/* PDF Modal */}
      <PoPdfModal poId={pdfPoId} token={token} onClose={() => setPdfPoId(null)} />
    </div>
  );
}

function NewPODrawer({ open, onClose, token, onSuccess }) {
  const toast = useToast();
  const { formatMoney, countryCode, taxRateDefault } = useTenantFormat();
  const { profile } = useTenant();
  const isIN = countryCode === 'IN';

  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [supplierName, setSupplierName] = useState('');
  const [supplierPhone, setSupplierPhone] = useState('');
  const [branchId, setBranchId] = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showProd, setShowProd] = useState(false);
  const [prodQ, setProdQ] = useState('');
  const [placeOfSupply, setPlaceOfSupply] = useState('');
  const [statesList, setStatesList] = useState([]);
  const [showPosPicker, setShowPosPicker] = useState(false);
  const [posQ, setPosQ] = useState('');

  useEffect(() => {
    if (open && token) {
      api.productsList(token).then(r => setProducts(r.items || [])).catch(() => {});
      api.branchesList(token).then(r => {
        const b = r.items || [];
        setBranches(b);
        const main = b.find(x => x.is_main);
        if (main) setBranchId(main.id);
        else if (b.length) setBranchId(b[0].id);
      }).catch(() => {});
      if (isIN) {
        api.states().then(r => setStatesList(r.states || [])).catch(() => {});
        api.tenantMe(token).then(r => setPlaceOfSupply(r?.state || '')).catch(() => {});
      }
      setSupplierName(''); setSupplierPhone(''); setNotes('');
      setLineItems([]); setShowProd(false); setProdQ('');
      setShowPosPicker(false); setPosQ('');
    }
  }, [open, token, isIN]);

  const addProduct = p => {
    setLineItems(prev => [...prev, { product_id: p.id, name: p.name, qty: '1', unit_price: String(p.cost_price || p.sell_price || 0), gst_rate: p.gst_rate || 18 }]);
    setShowProd(false); setProdQ('');
  };

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    for (const li of lineItems) { const line = (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0); sub += line; tax += line * (li.gst_rate || 0) / 100; }
    return { subtotal: sub, tax, total: sub + tax };
  }, [lineItems]);

  const submit = async () => {
    if (!supplierName.trim()) { toast.show('Supplier name required', 'error'); return; }
    if (!lineItems.length) { toast.show('Add at least one item', 'error'); return; }
    if (!branchId) { toast.show('Select a branch', 'error'); return; }
    setSaving(true);
    try {
      const body = {
        supplier_name: supplierName.trim(),
        supplier_phone: supplierPhone || undefined,
        branch_id: branchId,
        items: lineItems.map(li => ({ product_id: li.product_id, qty: parseFloat(li.qty) || 0, unit_price: parseFloat(li.unit_price) || undefined })),
        notes: notes || undefined,
      };
      const r = await api.purchaseOrdersCreate(token, body);
      toast.show(`PO created · ${r.po_number || ''}`, 'success');
      onSuccess();
    } catch (e) { toast.show(e?.message || 'Create failed', 'error'); } finally { setSaving(false); }
  };

  const fProd = products.filter(p => !prodQ || p.name?.toLowerCase().includes(prodQ.toLowerCase()));
  const fStates = statesList.filter(s => !posQ || s.toLowerCase().includes(posQ.toLowerCase()));

  return (
    <Drawer open={open} onClose={onClose} title="New Purchase Order" wide>
      <div className="space-y-5">
        {/* Supplier */}
        <div className="grid grid-cols-2 gap-4">
          <label className="block" htmlFor="npo-supplier">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Supplier Name *</span>
            <input id="npo-supplier" data-testid="npo-supplier-name" className={`mt-1.5 ${INPUT}`} value={supplierName} onChange={e => setSupplierName(e.target.value)} placeholder="Enter supplier name" />
          </label>
          <label className="block" htmlFor="npo-phone">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Supplier Phone</span>
            <input id="npo-phone" data-testid="npo-supplier-phone" className={`mt-1.5 ${INPUT}`} value={supplierPhone} onChange={e => setSupplierPhone(e.target.value)} placeholder="Optional" />
          </label>
        </div>

        {/* Branch */}
        <label className="block" htmlFor="npo-branch">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Branch</span>
          <select id="npo-branch" data-testid="npo-branch" className={`mt-1.5 ${INPUT} appearance-none cursor-pointer`} value={branchId} onChange={e => setBranchId(e.target.value)}>
            {branches.map(b => <option key={b.id} value={b.id} className="bg-white">{b.name}{b.is_main ? ' (Main)' : ''}</option>)}
          </select>
        </label>

        {/* Line items */}
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-2">Line Items</p>
          {lineItems.map((li, i) => (
            <div key={i} data-testid={`npo-line-${i}`} className="flex items-center gap-3 py-2 border-b border-gray-100">
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 text-sm font-medium truncate">{li.name}</p>
                <p className="text-gray-400 text-xs">{isIN ? 'GST' : 'VAT'} Rate {li.gst_rate}%</p>
              </div>
              <input data-testid={`npo-qty-${i}`} type="number" value={li.qty} onChange={e => { const c = [...lineItems]; c[i].qty = e.target.value; setLineItems(c); }} className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-gray-900 text-sm text-right outline-none focus:border-indigo-400" placeholder="Qty" />
              <input data-testid={`npo-price-${i}`} type="number" value={li.unit_price} onChange={e => { const c = [...lineItems]; c[i].unit_price = e.target.value; setLineItems(c); }} className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-gray-900 text-sm text-right outline-none focus:border-indigo-400" placeholder="₹" />
              <button onClick={() => setLineItems(lineItems.filter((_, j) => j !== i))} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"><X size={16} /></button>
            </div>
          ))}
          <button data-testid="npo-add-item" onClick={() => { setShowProd(!showProd); setShowPosPicker(false); }} className="mt-2 flex items-center gap-2 px-4 py-2 rounded-xl bg-teal-50 border border-teal-200 text-teal-600 text-sm font-semibold hover:bg-teal-100 transition-colors"><Plus size={14} /> Add product</button>
          {showProd && (
            <div className="mt-2 bg-gray-50 rounded-xl border border-gray-200 p-3 max-h-48 overflow-y-auto space-y-1">
              <input data-testid="npo-prod-search" className={INPUT} placeholder="Search products..." value={prodQ} onChange={e => setProdQ(e.target.value)} />
              {fProd.map(p => (<button key={p.id} data-testid={`npo-prod-${p.id}`} className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-gray-700 text-sm transition-colors" onClick={() => addProduct(p)}>{p.name} <span className="text-gray-400 text-xs">SKU {p.sku} · {formatMoney(p.cost_price || p.sell_price)}</span></button>))}
            </div>
          )}
        </div>

        {/* Place of Supply — IN only */}
        {isIN && (
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-2">Place of Supply</p>
            <button data-testid="npo-place-of-supply" onClick={() => { setShowPosPicker(!showPosPicker); setShowProd(false); }}
              className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 text-sm hover:border-gray-300 transition-colors flex items-center justify-between">
              <span className={placeOfSupply ? 'text-gray-900' : 'text-gray-400'}>{placeOfSupply || 'Select state...'}</span>
              <ChevronDown size={14} className="text-gray-400" />
            </button>
            {showPosPicker && (
              <div className="mt-2 bg-gray-50 rounded-xl border border-gray-200 p-3 max-h-48 overflow-y-auto space-y-1">
                <input data-testid="npo-pos-search" className={INPUT} placeholder="Search state..." value={posQ} onChange={e => setPosQ(e.target.value)} />
                {fStates.map(s => (
                  <button key={s} data-testid={`npo-pos-${s}`} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${placeOfSupply === s ? 'bg-teal-50 text-teal-600 font-semibold' : 'hover:bg-gray-100 text-gray-700'}`}
                    onClick={() => { setPlaceOfSupply(s); setShowPosPicker(false); setPosQ(''); }}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Totals */}
        {lineItems.length > 0 && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-2" data-testid="npo-totals-card">
            <div className="flex justify-between"><span className="text-gray-500 text-sm">Subtotal</span><span className="text-gray-900 text-sm">{formatMoney(totals.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500 text-sm">{isIN ? 'GST' : 'VAT'}</span><span className="text-gray-900 text-sm">{formatMoney(totals.tax)}</span></div>
            <div className="flex justify-between border-t border-gray-200 pt-2"><span className="text-gray-900 font-bold">Total</span><span className="text-teal-600 font-extrabold text-lg" data-testid="npo-grand-total">{formatMoney(totals.total)}</span></div>
          </div>
        )}

        {/* Notes */}
        <label className="block" htmlFor="npo-notes"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Notes</span><textarea id="npo-notes" data-testid="npo-notes" className={`mt-1.5 ${INPUT} h-20 resize-none`} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..." /></label>

        {/* Submit */}
        <button data-testid="npo-submit" onClick={submit} disabled={saving} className="w-full h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50">
          {saving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Create Purchase Order'}
        </button>
      </div>
    </Drawer>
  );
}


function StatusPill({ status }) {
  const s = (status || 'draft').toLowerCase();
  const map = {
    draft: 'bg-gray-100 text-gray-600 border-gray-200',
    sent: 'bg-blue-50 text-blue-600 border-blue-200',
    received: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    cancelled: 'bg-red-50 text-red-500 border-red-200',
  };
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider border ${map[s] || map.draft}`}>
      {status || 'Draft'}
    </span>
  );
}

function PoDetail({ po, formatMoney }) {
  const { taxLabel } = useTenantFormat();
  const taxMode = po.tax_mode_snapshot || (po.vat ? 'VAT' : 'IN');
  const partyTaxIdLabel = po.party_tax_id_label_snapshot || (taxMode === 'VAT' ? 'TRN' : 'GSTIN');
  // Aggregate tax from line-level snapshots if available
  let totalCgst = 0, totalSgst = 0, totalIgst = 0, totalVat = 0, hasSnapshots = false;
  for (const item of (po.items || [])) {
    const tb = item.tax_breakdown_snapshot;
    if (tb) {
      hasSnapshots = true;
      totalCgst += tb.cgst || 0; totalSgst += tb.sgst || 0;
      totalIgst += tb.igst || 0; totalVat += tb.vat || 0;
    }
  }
  if (!hasSnapshots) {
    totalCgst = po.cgst || 0; totalSgst = po.sgst || 0;
    totalIgst = po.igst || 0; totalVat = po.vat || 0;
  }
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div><span className="text-gray-500 text-xs font-bold uppercase">Supplier</span><p className="text-gray-900 font-semibold mt-1">{po.supplier_name || '—'}</p></div>
        <div><span className="text-gray-500 text-xs font-bold uppercase">Date</span><p className="text-gray-900 font-semibold mt-1">{po.date ? new Date(po.date).toLocaleDateString('en-IN') : '—'}</p></div>
        <div><span className="text-gray-500 text-xs font-bold uppercase">Status</span><div className="mt-1"><StatusPill status={po.status} /></div></div>
        <div><span className="text-gray-500 text-xs font-bold uppercase">Total</span><p className="text-gray-900 font-extrabold text-lg mt-1">{formatMoney(po.total)}</p></div>
        {po.supplier_gstin && <div><span className="text-gray-500 text-xs font-bold uppercase">{partyTaxIdLabel}</span><p className="text-gray-900 font-semibold mt-1">{po.supplier_gstin}</p></div>}
        {po.place_of_supply && taxMode === 'IN' && <div><span className="text-gray-500 text-xs font-bold uppercase">Place of Supply</span><p className="text-gray-900 font-semibold mt-1">{po.place_of_supply}</p></div>}
      </div>
      {po.items?.length > 0 && (
        <div>
          <h4 className="text-gray-500 text-xs font-bold uppercase mb-2">Line Items</h4>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Item</th>
                <th className="text-right px-4 py-2 text-gray-500 text-xs font-bold uppercase">Qty</th>
                <th className="text-right px-4 py-2 text-gray-500 text-xs font-bold uppercase">Rate</th>
                <th className="text-right px-4 py-2 text-gray-500 text-xs font-bold uppercase">{taxLabel}</th>
                <th className="text-right px-4 py-2 text-gray-500 text-xs font-bold uppercase">Amount</th>
              </tr></thead>
              <tbody>
                {po.items.map((item, i) => {
                  const tb = item.tax_breakdown_snapshot;
                  const taxStr = tb
                    ? (tb.mode === 'IN'
                      ? (tb.igst > 0 ? `IGST ${formatMoney(tb.igst)}` : `C${formatMoney(tb.cgst)}+S${formatMoney(tb.sgst)}`)
                      : `VAT ${formatMoney(tb.vat)}`)
                    : formatMoney(item.line_gst_amount || 0);
                  return (
                    <tr key={i} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-2 text-gray-900 font-semibold">{item.name || item.item_name || item.product_name || '—'}</td>
                      <td className="px-4 py-2 text-right text-gray-700 tabular-nums">{item.quantity ?? item.qty ?? '—'}</td>
                      <td className="px-4 py-2 text-right text-gray-700 tabular-nums">{formatMoney(item.rate || item.unit_price)}</td>
                      <td className="px-4 py-2 text-right text-gray-500 tabular-nums text-xs">{taxStr}</td>
                      <td className="px-4 py-2 text-right text-gray-900 font-semibold tabular-nums">{formatMoney(item.amount || item.line_total || (item.quantity || item.qty) * (item.rate || item.unit_price))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Tax breakdown summary */}
      <div className="border border-gray-200 rounded-xl p-4 space-y-1.5" data-testid="po-tax-summary">
        <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span className="text-gray-900 tabular-nums">{formatMoney(po.subtotal)}</span></div>
        {taxMode === 'IN' ? (
          <>
            {(totalCgst > 0 || totalSgst > 0) && (
              <>
                <div className="flex justify-between text-sm"><span className="text-gray-500">CGST</span><span className="text-gray-900 tabular-nums" data-testid="po-cgst">{formatMoney(totalCgst)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">SGST</span><span className="text-gray-900 tabular-nums" data-testid="po-sgst">{formatMoney(totalSgst)}</span></div>
              </>
            )}
            {totalIgst > 0 && (
              <div className="flex justify-between text-sm"><span className="text-gray-500">IGST</span><span className="text-gray-900 tabular-nums" data-testid="po-igst">{formatMoney(totalIgst)}</span></div>
            )}
            {!totalCgst && !totalSgst && !totalIgst && (
              <div className="flex justify-between text-sm"><span className="text-gray-500">GST</span><span className="text-gray-900 tabular-nums">{formatMoney(0)}</span></div>
            )}
          </>
        ) : (
          <div className="flex justify-between text-sm"><span className="text-gray-500">VAT</span><span className="text-gray-900 tabular-nums" data-testid="po-vat">{formatMoney(totalVat)}</span></div>
        )}
        <div className="flex justify-between text-sm border-t border-gray-200 pt-1.5 mt-1"><span className="text-gray-900 font-bold">Total</span><span className="text-gray-900 font-extrabold tabular-nums">{formatMoney(po.total)}</span></div>
      </div>
    </div>
  );
}

/* ── PO PDF Preview Modal (same pattern as Sales InvoicePdfModal) ── */
function PoPdfModal({ poId, token, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const BASE = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (!poId || !token) return;
    let cancelled = false;
    const url = `${BASE}/api/purchase-orders/${poId}/purchase.pdf`;
    setLoading(true); setError(null); setBlobUrl(null);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error('Failed to load PDF'); return r.blob(); })
      .then(blob => { if (!cancelled) setBlobUrl(URL.createObjectURL(blob)); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [poId, token, BASE]);

  useEffect(() => { return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }; }, [blobUrl]);

  if (!poId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="po-pdf-modal">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-200 w-[90vw] max-w-4xl h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-gray-900 font-bold text-lg" data-testid="po-pdf-title">Purchase Order PDF</h3>
          <div className="flex items-center gap-2">
            <button data-testid="po-pdf-download" disabled={!blobUrl} onClick={() => { if (blobUrl) { const a = document.createElement('a'); a.href = blobUrl; a.download = `PO-${poId}.pdf`; document.body.appendChild(a); a.click(); a.remove(); } }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors disabled:opacity-40">
              <Download size={14} /> Download
            </button>
            <button data-testid="po-pdf-print" disabled={!blobUrl} onClick={() => { const f = document.getElementById('po-pdf-iframe'); if (f?.contentWindow) { f.contentWindow.focus(); f.contentWindow.print(); } }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-purple text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40">
              <Printer size={14} /> Print
            </button>
            <button data-testid="po-pdf-close" onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"><X size={20} /></button>
          </div>
        </div>
        <div className="flex-1 bg-gray-50 flex items-center justify-center">
          {loading && <div className="w-6 h-6 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" />}
          {error && <p className="text-red-500 text-sm">{error}</p>}
          {blobUrl && <iframe id="po-pdf-iframe" title="PO PDF" src={blobUrl} className="w-full h-full border-0" data-testid="po-pdf-iframe" />}
        </div>
      </div>
    </div>
  );
}
